/**
 * LAURYE — AJAX Cart Drawer & Cart Page Handler
 * Full Shopify Cart API compliance with Free Shipping Progress Bar
 */

class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    this.overlay = document.querySelector('[data-cart-drawer-overlay]');
    this.closeBtn = document.querySelector('[data-cart-drawer-close]');
    this.openBtns = document.querySelectorAll('[data-cart-drawer-toggle]');
    this.itemsContainer = document.querySelector('[data-cart-drawer-items]');
    this.threshold = parseFloat(window.themeSettings?.freeShippingThreshold || 25);
    this.countdownInterval = null;

    this.init();
  }

  init() {
    // Open/close drawer events if drawer elements exist
    if (this.drawer && this.overlay) {
      this.openBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      this.overlay.addEventListener('click', () => this.close());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('active')) {
          this.close();
        }
      });
    }

    // Global delegation for ALL quantity steppers and remove buttons (drawer + cart page)
    document.addEventListener('click', async (e) => {
      const plusBtn = e.target.closest('[data-cart-qty-plus], [data-cart-page-qty-plus]');
      const minusBtn = e.target.closest('[data-cart-qty-minus], [data-cart-page-qty-minus]');
      const removeBtn = e.target.closest('[data-cart-item-remove], [data-cart-page-item-remove]');

      if (plusBtn) {
        e.preventDefault();
        e.stopPropagation();
        const row = plusBtn.closest('.cart-item');
        const stepper = plusBtn.closest('.quantity-stepper');
        const input = stepper ? stepper.querySelector('.quantity-input') : null;
        const currentQty = input ? (parseInt(input.value, 10) || 1) : 1;
        const nextQty = currentQty + 1;
        if (input) input.value = nextQty;
        const key = plusBtn.dataset.itemKey || input?.dataset.itemKey || row?.dataset.cartItemKey || row?.dataset.cartItemId;
        const line = plusBtn.dataset.line || input?.dataset.line;
        await this.updateQuantity(key || line, nextQty);
      } else if (minusBtn) {
        e.preventDefault();
        e.stopPropagation();
        const row = minusBtn.closest('.cart-item');
        const stepper = minusBtn.closest('.quantity-stepper');
        const input = stepper ? stepper.querySelector('.quantity-input') : null;
        const currentQty = input ? (parseInt(input.value, 10) || 1) : 1;
        const nextQty = Math.max(0, currentQty - 1);
        if (nextQty === 0 && row) {
          row.style.opacity = '0.3';
          row.style.pointerEvents = 'none';
        } else if (input) {
          input.value = nextQty;
        }
        const key = minusBtn.dataset.itemKey || input?.dataset.itemKey || row?.dataset.cartItemKey || row?.dataset.cartItemId;
        const line = minusBtn.dataset.line || input?.dataset.line;
        await this.updateQuantity(key || line, nextQty);
      } else if (removeBtn) {
        e.preventDefault();
        e.stopPropagation();
        const row = removeBtn.closest('.cart-item');
        const key = removeBtn.dataset.itemKey || row?.dataset.cartItemKey || row?.dataset.cartItemId;
        const line = removeBtn.dataset.line;
        if (row) {
          row.style.opacity = '0.3';
          row.style.pointerEvents = 'none';
        }
        await this.updateQuantity(key || line, 0);
      }
    });

    // Handle manual change on quantity inputs anywhere
    document.addEventListener('change', async (e) => {
      const input = e.target.closest('.quantity-input');
      if (!input) return;
      const row = input.closest('.cart-item');
      const key = input.dataset.itemKey || row?.dataset.cartItemKey || row?.dataset.cartItemId;
      const line = input.dataset.line;
      if (!key && !line) return;
      const newQty = Math.max(0, parseInt(input.value, 10) || 0);
      if (newQty === 0 && row) {
        row.style.opacity = '0.3';
        row.style.pointerEvents = 'none';
      }
      await this.updateQuantity(key || line, newQty);
    });

    // Intercept Quick Add and Product forms
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (form) {
        e.preventDefault();
        this.addItem(new FormData(form));
      }
    });

    // Discount code apply / remove handlers
    document.addEventListener('click', async (e) => {
      const applyDiscountBtn = e.target.closest('[data-cart-discount-apply]');
      const removeDiscountBtn = e.target.closest('[data-cart-discount-remove]');

      if (applyDiscountBtn) {
        e.preventDefault();
        const input = document.querySelector('[data-cart-discount-input]');
        const code = input ? input.value : '';
        await this.applyDiscount(code);
      } else if (removeDiscountBtn) {
        e.preventDefault();
        await this.removeDiscount();
      }
    });

    document.addEventListener('keydown', async (e) => {
      if (e.key === 'Enter') {
        const input = e.target.closest('[data-cart-discount-input]');
        if (input) {
          e.preventDefault();
          await this.applyDiscount(input.value);
        }
      }
    });

    // Initialize discount code UI from session/cart
    this.initDiscountUI();

    // Check initial cart state for countdown timer
    const initialCount = parseInt(document.querySelector('[data-cart-count]')?.textContent, 10) || 0;
    if (initialCount > 0) {
      this.startCountdown();
    } else {
      this.stopCountdown();
    }
  }

  open() {
    if (this.drawer && this.overlay) {
      this.drawer.classList.add('active');
      this.overlay.classList.add('active');
      document.body.style.overflow = 'hidden';
    }
  }

  close() {
    if (this.drawer && this.overlay) {
      this.drawer.classList.remove('active');
      this.overlay.classList.remove('active');
      document.body.style.overflow = '';
    }
  }

  formatMoney(cents) {
    if (typeof cents === 'string' && cents.includes('€')) return cents;
    const val = typeof cents === 'number' ? cents : parseFloat(cents) || 0;
    const euros = (val / 100).toFixed(2).replace('.', ',');
    return `${euros} €`;
  }

  startCountdown() {
    if (this.countdownInterval) {
      return; // Already ticking
    }

    let expiresAt = parseInt(sessionStorage.getItem('laurye_cart_countdown'), 10);
    const now = Date.now();

    if (!expiresAt || isNaN(expiresAt) || expiresAt <= now) {
      expiresAt = now + (10 * 60 * 1000);
      sessionStorage.setItem('laurye_cart_countdown', expiresAt);
    }

    const updateDisplay = () => {
      const currentTime = Date.now();
      const remainingSeconds = Math.max(0, Math.floor((expiresAt - currentTime) / 1000));
      const minutes = Math.floor(remainingSeconds / 60);
      const seconds = remainingSeconds % 60;
      const formatted = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

      document.querySelectorAll('[data-cart-countdown-timer]').forEach(el => {
        el.textContent = formatted;
      });

      if (remainingSeconds <= 0) {
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
          this.countdownInterval = null;
        }
      }
    };

    updateDisplay();
    this.countdownInterval = setInterval(updateDisplay, 1000);
  }

  stopCountdown() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    sessionStorage.removeItem('laurye_cart_countdown');
    document.querySelectorAll('[data-cart-countdown-timer]').forEach(el => {
      el.textContent = '10:00';
    });
  }

  initDiscountUI() {
    const savedDiscount = sessionStorage.getItem('laurye_cart_discount');
    const appliedWrap = document.querySelector('[data-cart-discount-applied]');
    const nameEl = document.querySelector('[data-cart-discount-name]');
    const formEl = document.querySelector('[data-cart-discount-form]');
    const inputEl = document.querySelector('[data-cart-discount-input]');
    const msgEl = document.querySelector('[data-cart-discount-message]');

    if (savedDiscount) {
      if (nameEl) nameEl.textContent = savedDiscount;
      if (appliedWrap) appliedWrap.style.display = 'flex';
      if (formEl) formEl.style.display = 'none';
      if (inputEl) inputEl.value = '';
      if (msgEl) {
        msgEl.style.display = 'none';
        msgEl.textContent = '';
      }
      this.updateCheckoutDiscount(savedDiscount);
    } else {
      if (appliedWrap) appliedWrap.style.display = 'none';
      if (formEl) formEl.style.display = 'flex';
      this.updateCheckoutDiscount('');
    }
  }

  async applyDiscount(rawCode) {
    const code = (rawCode || '').trim().toUpperCase();
    const msgEl = document.querySelector('[data-cart-discount-message]');
    const inputEl = document.querySelector('[data-cart-discount-input]');
    const appliedWrap = document.querySelector('[data-cart-discount-applied]');
    const nameEl = document.querySelector('[data-cart-discount-name]');
    const formEl = document.querySelector('[data-cart-discount-form]');
    const applyBtn = document.querySelector('[data-cart-discount-apply]');

    if (!code) {
      if (msgEl) {
        msgEl.textContent = 'Por favor, introduz um código de desconto.';
        msgEl.style.color = '#d32f2f';
        msgEl.style.display = 'block';
      }
      return;
    }

    if (applyBtn) {
      applyBtn.disabled = true;
      applyBtn.textContent = 'A aplicar...';
    }

    if (msgEl) {
      msgEl.style.display = 'none';
      msgEl.textContent = '';
    }

    try {
      // 1. Set discount cookie on Shopify domain
      await fetch(`/discount/${encodeURIComponent(code)}`, { method: 'GET' });

      // 2. Update cart attributes for tracking
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: { 'applied_discount': code } })
      });

      // 3. Store in sessionStorage for persistent state
      sessionStorage.setItem('laurye_cart_discount', code);

      // 4. Update checkout forms with discount
      this.updateCheckoutDiscount(code);

      // 5. Update UI
      if (nameEl) nameEl.textContent = code;
      if (appliedWrap) appliedWrap.style.display = 'flex';
      if (formEl) formEl.style.display = 'none';
      if (inputEl) inputEl.value = '';
      if (msgEl) {
        msgEl.style.display = 'none';
        msgEl.textContent = '';
      }

      if (window.showToast) {
        window.showToast(`Código "${code}" aplicado com sucesso!`);
      }

      await this.refreshCart();
    } catch (error) {
      console.error('Error applying discount:', error);
      if (msgEl) {
        msgEl.textContent = 'Código inválido ou indisponível. Tenta novamente.';
        msgEl.style.color = '#d32f2f';
        msgEl.style.display = 'block';
      }
    } finally {
      if (applyBtn) {
        applyBtn.disabled = false;
        applyBtn.textContent = 'Aplicar';
      }
    }
  }

  async removeDiscount() {
    const msgEl = document.querySelector('[data-cart-discount-message]');
    const inputEl = document.querySelector('[data-cart-discount-input]');
    const appliedWrap = document.querySelector('[data-cart-discount-applied]');
    const formEl = document.querySelector('[data-cart-discount-form]');

    try {
      // 1. Clear discount cookie in Shopify
      document.cookie = "discount_code=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT";
      if (window.location.hostname) {
        document.cookie = "discount_code=; path=/; domain=" + window.location.hostname + "; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT";
      }

      // 2. Clear cart attributes
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: { 'applied_discount': '' } })
      });

      // 3. Clear session storage
      sessionStorage.removeItem('laurye_cart_discount');

      // 4. Remove discount from checkout forms
      this.updateCheckoutDiscount('');

      // 5. Update UI state immediately
      if (appliedWrap) appliedWrap.style.display = 'none';
      if (formEl) formEl.style.display = 'flex';
      if (inputEl) inputEl.value = '';
      if (msgEl) {
        msgEl.style.display = 'none';
        msgEl.textContent = '';
      }

      if (window.showToast) {
        window.showToast('Código de desconto removido.');
      }

      // 6. Refresh cart data and recalculate totals
      await this.refreshCart();
    } catch (error) {
      console.error('Error removing discount:', error);
      sessionStorage.removeItem('laurye_cart_discount');
      this.updateCheckoutDiscount('');
      if (appliedWrap) appliedWrap.style.display = 'none';
      if (formEl) formEl.style.display = 'flex';
      await this.refreshCart();
    }
  }

  updateCheckoutDiscount(code) {
    document.querySelectorAll('[data-cart-checkout-discount]').forEach(input => {
      input.value = code || '';
    });

    document.querySelectorAll('form[action*="/checkout"]').forEach(form => {
      let hidden = form.querySelector('input[name="discount"]');
      if (code) {
        if (!hidden) {
          hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'discount';
          form.appendChild(hidden);
        }
        hidden.value = code;
      } else {
        if (hidden) {
          hidden.remove();
        }
      }
    });
  }

  async addItem(formData) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });
      const item = await response.json();
      await this.refreshCart();
      this.open();
      if (window.showToast) {
        window.showToast(`"${item.title || 'Joia'}" adicionada à cesta!`);
      }
    } catch (error) {
      console.error('Error adding item to cart:', error);
      await this.refreshCart();
      this.open();
    }
  }

  async updateQuantity(identifier, quantity) {
    try {
      let payload;
      if (String(identifier).includes(':') || String(identifier).length > 5) {
        payload = { id: String(identifier), quantity: Number(quantity) };
      } else if (!isNaN(Number(identifier)) && Number(identifier) > 0) {
        payload = { line: Number(identifier), quantity: Number(quantity) };
      } else {
        payload = { id: String(identifier), quantity: Number(quantity) };
      }

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const cart = await response.json();
        this.renderCart(cart);
      } else {
        await this.refreshCart();
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      await this.refreshCart();
    }
  }

  async refreshCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.renderCart(cart);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }

  renderCart(cart) {
    // 1. Update Cart Count Badges across Header, Drawer, etc.
    const count = parseInt(cart.item_count, 10) || 0;
    document.querySelectorAll('[data-cart-count]').forEach(badge => {
      badge.textContent = count;
      badge.style.display = count > 0 ? 'flex' : 'none';
    });

    const hasDiscount = !!sessionStorage.getItem('laurye_cart_discount');
    const itemsOriginalTotal = (cart.items && cart.items.length > 0)
      ? cart.items.reduce((sum, item) => sum + (item.original_line_price || (item.price * item.quantity)), 0)
      : (cart.original_total_price || cart.total_price || 0);

    const totalPriceCents = hasDiscount
      ? (cart.total_price || itemsOriginalTotal)
      : (cart.original_total_price || itemsOriginalTotal || cart.total_price || 0);

    // 2. Update Free Shipping Progress Bar & Text
    const totalPriceEuros = totalPriceCents / 100;
    const remaining = this.threshold - totalPriceEuros;
    const progressPercent = Math.min(100, Math.max(0, (totalPriceEuros / this.threshold) * 100));

    document.querySelectorAll('[data-free-shipping-bar], #freeShippingProgressBar').forEach(bar => {
      bar.style.width = `${progressPercent}%`;
    });

    document.querySelectorAll('[data-free-shipping-text], #freeShippingText').forEach(txt => {
      if (remaining <= 0) {
        txt.innerHTML = `Parabéns! Tens <strong>Envio Gratuito</strong> garantido! 🎁`;
      } else {
        const formattedRemaining = remaining.toFixed(2).replace('.', ',') + ' €';
        txt.innerHTML = `Faltam apenas <strong>${formattedRemaining}</strong> para teres <strong>Envio Gratuito</strong>!`;
      }
    });

    // 3. Update Subtotal and Totals
    const formattedTotal = this.formatMoney(totalPriceCents);
    document.querySelectorAll('[data-cart-drawer-subtotal], [data-cart-subtotal], .cart-drawer-subtotal-price, #cartSubtotalText, #checkoutTotalText').forEach(el => {
      el.textContent = formattedTotal;
    });

    // 4. Update Countdown Timer
    if (count > 0) {
      document.querySelectorAll('[data-cart-countdown-wrapper]').forEach(el => el.style.display = 'flex');
      this.startCountdown();
    } else {
      document.querySelectorAll('[data-cart-countdown-wrapper]').forEach(el => el.style.display = 'none');
      this.stopCountdown();
    }

    // 5. Update Discount Code UI & Checkout Fields
    this.initDiscountUI();

    // 6. Update Cart Page (/cart)
    this.renderCartPage(cart, totalPriceCents, hasDiscount);

    // 7. Update Drawer items
    this.renderCartDrawer(cart, hasDiscount);
  }

  renderCartPage(cart, totalPriceCents, hasDiscount) {
    const cartPageSection = document.querySelector('[data-cart-page-section]');
    const cartPageContent = document.querySelector('[data-cart-page-content]');
    const cartPageItems = document.querySelector('[data-cart-page-items]');
    const cartForm = document.querySelector('#cart-form');

    if (!cartPageSection && !cartPageContent && !cartPageItems && !cartForm) {
      return; // Not on /cart page
    }

    const count = parseInt(cart.item_count, 10) || 0;

    if (count === 0 || !cart.items || cart.items.length === 0) {
      const emptyHtml = `
        <div class="cart-drawer-empty" style="text-align: center; padding: 5rem 1.5rem; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.75rem;" data-cart-page-empty>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#E36269" stroke-width="1.5" style="margin-bottom: 0.5rem;">
            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <path d="M16 10a4 4 0 0 1-8 0"></path>
          </svg>
          <h2 style="font-size: 1.6rem; font-weight: 800; margin-bottom: 0.25rem;"> A tua cesta está vazia </h2>
          <p style="color: #666; max-width: 320px; font-size: 0.95rem; line-height: 1.5;">Descobre as peças que vão completar o teu próximo look.</p>
          <a href="/collections/conjuntos" class="btn btn-primary" style="margin-top: 1rem; padding: 0.9rem 2.2rem;">Explorar joias</a>
        </div>
      `;

      if (cartPageContent) {
        cartPageContent.innerHTML = emptyHtml;
      } else if (cartForm) {
        cartForm.outerHTML = emptyHtml;
      }
      return;
    }

    const subtotalFormatted = this.formatMoney(totalPriceCents || cart.total_price);

    // If cartForm doesn't exist yet (e.g. was loaded empty, but item added), construct form
    if (!cartForm && cartPageContent) {
      const itemsHtml = cart.items.map((item, index) => this.buildSingleCartPageItemHtml(item, index, hasDiscount)).join('');
      cartPageContent.innerHTML = `
        <form action="/cart" method="post" id="cart-form" style="margin-top: 2rem;">
          <div style="display: flex; flex-direction: column; gap: 1.5rem;" data-cart-page-items>
            ${itemsHtml}
          </div>
          <div style="margin-top: 2.5rem; padding: 2rem; background-color: #FAF8F5; border-radius: var(--radius-md); border: 1px solid var(--color-border); display: flex; flex-direction: column; gap: 1rem;" data-cart-page-footer>
            <div style="display: flex; justify-content: space-between; align-items: center; font-size: 1.3rem; font-weight: 700;">
              <span>Subtotal</span>
              <span class="text-pink" data-cart-drawer-subtotal>${subtotalFormatted}</span>
            </div>
            <p style="font-size: 0.82rem; color: #666;">
              IVA incluído. Portes de envio calculados na finalização da compra.
            </p>
            <div style="display: flex; gap: 1rem; margin-top: 1rem; flex-wrap: wrap;">
              <a href="/collections/all" class="btn btn-secondary" style="flex: 1;">Continuar a comprar</a>
              <button type="submit" name="checkout" class="btn btn-primary" style="flex: 1.5; padding: 1rem 2rem; font-size: 0.95rem;">
                Finalizar compra &bull; <span data-cart-drawer-subtotal>${subtotalFormatted}</span>
              </button>
            </div>
          </div>
        </form>
      `;
      return;
    }

    // Update existing rows and remove deleted rows
    if (cartPageItems) {
      const existingRows = cartPageItems.querySelectorAll('.cart-item');
      const itemKeys = new Set(cart.items.map(item => String(item.key || item.id)));

      // 1. Remove deleted rows from DOM immediately
      existingRows.forEach(row => {
        const rowKey = String(row.dataset.cartItemKey || row.dataset.cartItemId || '');
        if (!itemKeys.has(rowKey)) {
          row.remove();
        }
      });

      // 2. Update remaining rows or insert new ones
      cart.items.forEach((item, index) => {
        const itemKey = String(item.key || item.id);
        const row = cartPageItems.querySelector(`[data-cart-item-key="${itemKey}"]`) || 
                    cartPageItems.querySelector(`[data-cart-item-id="${item.id}"]`);
        
        const linePrice = this.formatMoney(hasDiscount ? (item.final_line_price || (item.price * item.quantity)) : (item.original_line_price || (item.price * item.quantity)));
        const singlePrice = this.formatMoney(hasDiscount ? (item.final_price || item.price) : (item.original_price || item.price));

        if (row) {
          row.style.opacity = '1';
          row.style.pointerEvents = '';
          row.dataset.cartItemKey = itemKey;
          row.dataset.cartItemId = item.id;
          
          const input = row.querySelector('.quantity-input');
          if (input) {
            input.value = item.quantity;
            input.dataset.itemKey = itemKey;
            input.dataset.line = index + 1;
          }

          const minusBtn = row.querySelector('[data-cart-qty-minus]');
          if (minusBtn) {
            minusBtn.dataset.itemKey = itemKey;
            minusBtn.dataset.line = index + 1;
          }

          const plusBtn = row.querySelector('[data-cart-qty-plus]');
          if (plusBtn) {
            plusBtn.dataset.itemKey = itemKey;
            plusBtn.dataset.line = index + 1;
          }

          const removeBtn = row.querySelector('[data-cart-item-remove]');
          if (removeBtn) {
            removeBtn.dataset.itemKey = itemKey;
            removeBtn.dataset.line = index + 1;
          }

          const linePriceEl = row.querySelector('[data-cart-line-price], .cart-item-line-price');
          if (linePriceEl) {
            linePriceEl.textContent = linePrice;
          }

          const singlePriceEl = row.querySelector('[data-cart-item-price], .cart-item-price');
          if (singlePriceEl) {
            singlePriceEl.textContent = singlePrice;
          }
        } else {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = this.buildSingleCartPageItemHtml(item, index, hasDiscount);
          if (tempDiv.firstElementChild) {
            cartPageItems.appendChild(tempDiv.firstElementChild);
          }
        }
      });
    }
  }

  buildSingleCartPageItemHtml(item, index, hasDiscount) {
    const itemKey = item.key || item.id;
    const linePrice = this.formatMoney(hasDiscount ? (item.final_line_price || (item.price * item.quantity)) : (item.original_line_price || (item.price * item.quantity)));
    const singlePrice = this.formatMoney(hasDiscount ? (item.final_price || item.price) : (item.original_price || item.price));
    const imageUrl = item.featured_image?.url || item.image || '';
    const title = item.product_title || item.title || 'Joia';
    const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';

    return `
      <div class="cart-item cart-page-item" data-cart-item-id="${item.id}" data-cart-item-key="${itemKey}" style="grid-template-columns: 100px 1fr auto; padding: 1.5rem; background-color: #FAF8F5; border-radius: var(--radius-md); border: 1px solid var(--color-border); transition: opacity 0.25s ease, transform 0.25s ease;">
        <img src="${imageUrl}" alt="${title}" style="width: 100px; height: 120px; object-fit: cover; border-radius: var(--radius-sm);">
        
        <div class="cart-item-info">
          <a href="${item.url || '#'}" class="cart-item-title" style="font-size: 1.05rem;">${title}</a>
          ${variantTitle ? `<span class="cart-item-variant" style="font-size: 0.85rem; color: #777;">${variantTitle}</span>` : ''}
          <div class="cart-item-price" data-cart-item-price="${item.final_price || item.price}" style="font-size: 1rem; color: var(--color-primary-accent); font-weight: 700;">${singlePrice}</div>
          
          <div style="margin-top: 0.5rem;">
            <button type="button" class="cart-item-remove-btn" data-cart-item-remove data-item-key="${itemKey}" data-line="${index + 1}">Remover</button>
          </div>
        </div>

        <div style="display: flex; flex-direction: column; align-items: flex-end; justify-content: space-between;">
          <div class="quantity-stepper">
            <button type="button" class="quantity-btn" data-cart-qty-minus data-item-key="${itemKey}" data-line="${index + 1}">-</button>
            <input type="number" class="quantity-input" name="updates[${itemKey}]" value="${item.quantity}" min="1" data-item-key="${itemKey}" data-line="${index + 1}">
            <button type="button" class="quantity-btn" data-cart-qty-plus data-item-key="${itemKey}" data-line="${index + 1}">+</button>
          </div>
          <div class="cart-item-line-price" data-cart-line-price="${itemKey}" style="font-weight: 700; font-size: 1.05rem;">
            ${linePrice}
          </div>
        </div>
      </div>
    `;
  }

  renderCartDrawer(cart, hasDiscount) {
    if (!this.itemsContainer) return;

    if (!cart.items || cart.items.length === 0) {
      this.itemsContainer.innerHTML = `
        <div class="cart-drawer-empty" style="text-align: center; padding: 4rem 1.5rem; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.75rem;">
          <svg width="54" height="54" viewBox="0 0 24 24" fill="none" stroke="#E36269" stroke-width="1.5" style="margin-bottom: 0.5rem;">
            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <path d="M16 10a4 4 0 0 1-8 0"></path>
          </svg>
          <p style="font-weight: 800; font-size: 1.25rem; color: #121212;"> A tua cesta está vazia </p>
          <p style="font-size: 0.88rem; color: #666; max-width: 280px; line-height: 1.5;">Descobre as peças que vão completar o teu próximo look.</p>
          <a href="/collections/conjuntos" class="btn btn-primary" style="margin-top: 1rem; padding: 0.85rem 2rem;" onclick="window.cartDrawer?.close()">Explorar joias</a>
        </div>
      `;
      const footer = document.querySelector('.cart-drawer-footer');
      if (footer) footer.style.display = 'none';
      document.querySelectorAll('[data-cart-countdown-wrapper]').forEach(el => el.style.display = 'none');
      this.stopCountdown();
      return;
    }

    const footer = document.querySelector('.cart-drawer-footer');
    if (footer) footer.style.display = 'flex';
    document.querySelectorAll('[data-cart-countdown-wrapper]').forEach(el => el.style.display = 'flex');
    this.startCountdown();

    this.itemsContainer.innerHTML = cart.items.map(item => {
      const displayPrice = hasDiscount ? (item.final_price || item.price) : (item.original_price || item.price);
      return `
      <div class="cart-item" data-cart-item-id="${item.id}" data-cart-item-key="${item.key || item.id}">
        <img src="${item.featured_image?.url || item.image || ''}" alt="${item.title || ''}" class="cart-item-image">
        <div class="cart-item-info">
          <a href="${item.url || '#'}" class="cart-item-title">${item.product_title || item.title}</a>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<span class="cart-item-variant" style="font-size: 0.78rem; color: #777;">${item.variant_title}</span>` : ''}
          <div class="cart-item-price">${this.formatMoney(displayPrice)}</div>
          <div class="cart-item-bottom-row">
            <div class="quantity-stepper">
              <button type="button" class="quantity-btn" data-cart-qty-minus data-item-key="${item.key || item.id}">-</button>
              <input type="number" class="quantity-input" value="${item.quantity}" min="1" data-item-key="${item.key || item.id}">
              <button type="button" class="quantity-btn" data-cart-qty-plus data-item-key="${item.key || item.id}">+</button>
            </div>
            <button type="button" class="cart-item-remove-btn" data-cart-item-remove data-item-key="${item.key || item.id}">Remover</button>
          </div>
        </div>
      </div>
    `}).join('');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.cartDrawer = new CartDrawer();
});
